import React from 'react';
import { Checkbox } from '@shared/components';
import { formatCurrency } from '@/app/utils/formatCurrency';
import type { SmriqWorkItem } from '../../../types/smriq.types';
import { isReincludableDeclinedItem } from '../../../utils/smriqWorkItem';

interface ProviderInclusionsPanelProps {
    items: SmriqWorkItem[];
    declinedItems: SmriqWorkItem[];
    /** Scopes the checkbox DOM ids to this visit so a repeated item.id across
     *  visits (recurring operations) never collides in the document. */
    visitId: string;
    /** The visit's own visible heading, repeated into each accessible name so a
     *  recurring operation is distinguishable from the same row in another visit. */
    visitLabel: string;
    isDeclined: (itemId: string) => boolean;
    onToggleDecline: (itemId: string) => void;
    /** False once the decline set is at its cap and this visit's row is not already in it. */
    isDeclineAllowed?: (itemId: string) => boolean;
    /** False only when the division publishes
     *  `supports_recommended_operation_exclusions: false`; every item is then read-only. */
    exclusionsSupported?: boolean;
    disabled?: boolean;
    currency?: string;
}

/** Injective: every character outside [A-Za-z0-9] becomes "_<hex>_", so distinct
 *  opaque ids ("144:216000", "144_216000") can never encode to the same id. */
const encodeForDomId = (value: string): string =>
    value.replace(/[^a-zA-Z0-9]/gu, (char) => `_${(char.codePointAt(0) ?? 0).toString(16)}_`);

/** Neither half of an encoded id can contain "-", so the pair splits unambiguously. */
const inclusionDomId = (visitId: string, itemId: string): string =>
    `smriq_inclusion_${encodeForDomId(visitId)}-${encodeForDomId(itemId)}`;

/** One visit's inclusions from the in-memory `generate` response, never from
 *  `ConfiguredService.items`. Toggles only where `can_decline === true`, declined alike. */
export const ProviderInclusionsPanel: React.FC<ProviderInclusionsPanelProps> = ({
    items,
    declinedItems,
    visitId,
    visitLabel,
    isDeclined,
    onToggleDecline,
    isDeclineAllowed = () => true,
    exclusionsSupported = true,
    disabled = false,
    currency = 'GBP',
}) => {
    if (items.length === 0 && declinedItems.length === 0) return null;

    // Declining an item moves it from the vendor's `items` to `declined_items`, so rendering those
    // lists in sequence makes the row jump on every toggle. Merge them and order read-only rows
    // first, then the toggleable ones, each alphabetical by name (id as a stable tiebreak). A row's
    // group never changes under a toggle (a declined op stays re-includable), so each keeps its
    // place whether included or declined.
    const orderedItems = [
        ...items.map((item) => ({ item, declined: false })),
        ...declinedItems.map((item) => ({ item, declined: true })),
    ]
        .map(({ item, declined }) => ({
            item,
            toggleable:
                exclusionsSupported && (declined ? isReincludableDeclinedItem(item) : item.can_decline),
        }))
        .sort(
            (a, b) =>
                Number(a.toggleable) - Number(b.toggleable) ||
                a.item.name.localeCompare(b.item.name) ||
                a.item.id.localeCompare(b.item.id),
        );

    // Row styling mirrors ServiceItemsList so provider inclusions read the same as a
    // local plan's items inside the shared ServiceItem card.
    const rowClass = 'flex justify-between items-center py-2 px-3 bg-mp-background rounded border border-mp-border';

    // Vendor per-item gross (inc VAT). Shown for context; item prices do not sum to the visit total.
    const itemPrice = (item: SmriqWorkItem): React.ReactNode =>
        item.gross_price_money ? (
            <span className="text-sm font-medium text-mp-foreground">
                {formatCurrency(Number(item.gross_price_money.amount), currency)}
            </span>
        ) : null;

    // The item type sits in the sub-line ServiceItemsList uses for Qty/price. A recommended
    // (declinable) operation reads as "Included in plan" rather than its raw type.
    const typeLabel = (type: string): string =>
        type === 'recommended' ? 'Included in plan' : type.charAt(0).toUpperCase() + type.slice(1);

    const readOnlyItem = (item: SmriqWorkItem) => (
        <li key={item.id} className={rowClass}>
            <div className="flex-1">
                <span className="text-sm text-mp-foreground">{item.name}</span>
                <div className="text-xs text-mp-muted">{typeLabel(item.type)}</div>
            </div>
            {itemPrice(item)}
        </li>
    );

    const toggleableItem = (item: SmriqWorkItem) => {
        const domId = inclusionDomId(visitId, item.id);
        const itemDisabled = disabled || !isDeclineAllowed(item.id);

        return (
            <li key={item.id} className={rowClass}>
                <div className="flex items-start gap-3 flex-1">
                    <Checkbox
                        id={domId}
                        ariaLabel={`Include ${item.name}, ${visitLabel}`}
                        checked={!isDeclined(item.id)}
                        disabled={itemDisabled}
                        onChange={() => onToggleDecline(item.id)}
                    />
                    {/* Name and type share one column so the type lines up under the name, not the box. */}
                    <label
                        htmlFor={domId}
                        className={itemDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    >
                        <span className="text-sm text-mp-foreground">{item.name}</span>
                        <div className="text-xs text-mp-muted">{typeLabel(item.type)}</div>
                    </label>
                </div>
                {itemPrice(item)}
            </li>
        );
    };

    return (
        <div className="space-y-3 text-sm">
            <ul className="space-y-2">
                {/* An included op is declinable while the vendor says can_decline; a declined op with
                    a real vendor id stays re-includable, even though the vendor reports can_decline
                    false once it is declined. */}
                {orderedItems.map(({ item, toggleable }) =>
                    toggleable ? toggleableItem(item) : readOnlyItem(item),
                )}
            </ul>
        </div>
    );
};
